/*
 * label-maker/layout.js — what fits inside one label.
 *
 * WHY LAYOUT IS A SEPARATE, PURE MODULE
 * ─────────────────────────────────────
 * The preview is SVG in a browser; the output is a PDF. If each one decides
 * independently where the line breaks and how big the type is, they disagree —
 * and the user finds out after printing. So line breaking, auto-shrink and
 * overflow are decided ONCE, here, from injected font metrics, and both
 * backends are handed the finished lines. Preview and print then agree by
 * construction rather than by luck.
 *
 * THE PROBLEM THIS EXISTS TO CATCH
 * ────────────────────────────────
 * A merged sheet is 40 different strings in the same box. "Аня" fits; "Александра
 * Константинопольская" does not. Every competing tool prints the sheet anyway
 * — clipped, overlapping, or silently spilling past the die cut — and the
 * failure is discovered on paper, one sheet of label stock too late.
 * `fitText` therefore always returns an explicit verdict, and `auditRun`
 * reports WHICH records overflow before anything is exported.
 *
 * UNITS. Everything is millimetres, origin at the label's top-left, y down.
 * Font size is in millimetres too (cap-to-baseline nominal em size), because
 * a label is a physical object and points only invite conversion bugs.
 *
 * MEASUREMENT. `measure(text, style) -> width in mm` is injected. The caller
 * passes a function backed by the very font it will embed in the PDF, so the
 * numbers here are the numbers that reach paper.
 *
 * Pure module: no DOM, no fetch. Tested by node with a synthetic measurer.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelLayout = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Distance from the die cut that content must keep clear. Sheet labels are
     * cut with a tolerance of a few tenths of a millimetre and the printer adds
     * its own; 1.5 mm is the smallest inset at which text reliably survives
     * both. It is a DEFAULT, not a law — a full-bleed background deliberately
     * ignores it, which is why `bleed` elements exist. */
    var SAFE_MM = 1.5;

    /* The floor auto-shrink may not go below, and the size below which the user
     * is told the type got small.
     *
     * These are anchored rather than picked: EU Regulation 1169/2011 requires a
     * minimum x-height of 1.2 mm for mandatory food information, and a typical
     * face has an x-height near 0.52 em — so 1.2 mm of x-height is about a
     * 2.3 mm em size. Jars and preserves are one of this tool's main jobs, so
     * that is the right neighbourhood for "still meant to be read".
     *
     * Before 2026-08-19 the default floor was 1.6 mm, and a 60-character name
     * on a 38 × 21 mm address label was silently shrunk to it and reported as
     * FITTING. It did fit. It was also unreadable, and nothing said so. */
    var MIN_FONT_MM = 2.2;      // hard floor for automatic shrinking
    var LEGIBLE_MM = 2.6;       // below this the UI says the type went small

    /* ── text fitting ────────────────────────────────────────────────────────
     * Greedy wrapping at spaces, then at any character for words that cannot
     * fit on a line by themselves (long codes, German compounds, URLs). A word
     * that is broken is broken — never dropped, and never allowed to run past
     * the box unnoticed. */
    function wrapText(text, maxW, style, measure) {
        var out = [];
        var paragraphs = String(text == null ? '' : text).split('\n');
        for (var p = 0; p < paragraphs.length; p++) {
            var words = paragraphs[p].split(/(\s+)/).filter(function (s) { return s !== ''; });
            if (!words.length) { out.push(''); continue; }
            var line = '';
            for (var i = 0; i < words.length; i++) {
                var word = words[i];
                var isSpace = /^\s+$/.test(word);
                var probe = line + word;
                if (measure(probe, style) <= maxW || line === '') {
                    if (isSpace && line === '') continue;   // no leading space on a fresh line
                    line = probe;
                    // A single word longer than the box: break it by character.
                    if (line !== '' && measure(line, style) > maxW && !isSpace) {
                        var broken = breakLong(line, maxW, style, measure);
                        for (var b = 0; b < broken.length - 1; b++) out.push(broken[b]);
                        line = broken[broken.length - 1];
                    }
                } else {
                    out.push(rtrim(line));
                    line = isSpace ? '' : word;
                    if (line !== '' && measure(line, style) > maxW) {
                        var br = breakLong(line, maxW, style, measure);
                        for (var k = 0; k < br.length - 1; k++) out.push(br[k]);
                        line = br[br.length - 1];
                    }
                }
            }
            out.push(rtrim(line));
        }
        return out;
    }

    function breakLong(word, maxW, style, measure) {
        var lines = [], cur = '';
        var chars = Array.from(word);
        for (var i = 0; i < chars.length; i++) {
            var probe = cur + chars[i];
            if (cur !== '' && measure(probe, style) > maxW) { lines.push(cur); cur = chars[i]; }
            else cur = probe;
        }
        lines.push(cur);
        return lines;
    }

    function rtrim(s) { return s.replace(/\s+$/, ''); }

    /* Fit `text` into `box` {w,h} mm.
     *
     * `mode`:
     *   'shrink' (default) — start at `size`, step down until the wrapped text
     *                        fits both dimensions, no lower than `minSize`.
     *   'fixed'            — keep `size`; report overflow instead of resizing.
     *
     * Always returns a verdict. `fits:false` is not an exception, it is an
     * answer the UI is expected to surface.
     */
    function fitText(text, box, style, measure) {
        style = style || {};
        var size = num(style.size, 3);
        var minSize = Math.max(MIN_FONT_MM, num(style.minSize, MIN_FONT_MM));
        var lineGap = num(style.lineHeight, 1.22);
        var maxLines = style.maxLines != null ? Math.max(1, style.maxLines | 0) : 0;
        var mode = style.mode === 'fixed' ? 'fixed' : 'shrink';
        var w = Math.max(0.1, num(box.w, 10));
        var h = Math.max(0.1, num(box.h, 10));

        var attempt = function (s) {
            var st = assign({}, style, { size: s });
            var lines = wrapText(text, w, st, measure);
            var lineH = s * lineGap;
            var used = lines.length * lineH;
            var widest = 0;
            for (var i = 0; i < lines.length; i++) widest = Math.max(widest, measure(lines[i], st));
            return {
                lines: lines, size: s, lineHeight: lineH,
                height: used, width: widest,
                fits: used <= h + 1e-9 && widest <= w + 1e-9 && (!maxLines || lines.length <= maxLines),
            };
        };

        var r = attempt(size);
        if (mode === 'fixed' || r.fits) return verdict(r, size);

        // Binary search would be wrong here: wrapping is not monotone in a
        // useful way for the *line count*, but "does it fit" IS monotone in
        // size, so a coarse descent then a fine one is both correct and cheap.
        var s = size;
        while (s > minSize) {
            s = Math.max(minSize, s - (s > 6 ? 0.5 : 0.2));
            r = attempt(s);
            if (r.fits) break;
        }
        return verdict(r, size);
    }

    function verdict(r, requested) {
        return {
            lines: r.lines,
            size: r.size,
            lineHeight: r.lineHeight,
            width: r.width,
            height: r.height,
            fits: r.fits,
            shrunk: r.size < requested - 1e-9,
            requestedSize: requested,
        };
    }

    /* ── element placement ───────────────────────────────────────────────────
     * An element's box is expressed in fractions of the label (0..1) so one
     * design survives being moved to a different sheet format — the whole point
     * of presets. Absolute mm are resolved here, once.
     */
    function resolveBox(el, label) {
        var inset = el.bleed ? 0 : num(label.safe, SAFE_MM);
        var innerX = inset, innerY = inset;
        var innerW = Math.max(0, label.w - inset * 2);
        var innerH = Math.max(0, label.h - inset * 2);
        return {
            x: innerX + num(el.x, 0) * innerW,
            y: innerY + num(el.y, 0) * innerH,
            w: num(el.w, 1) * innerW,
            h: num(el.h, 1) * innerH,
        };
    }

    /* Lay out one label. Returns drawable items in label-local millimetres,
     * plus everything the UI needs to warn about before printing. */
    function layoutLabel(label, elements, ctx) {
        ctx = ctx || {};
        var measure = ctx.measure || defaultMeasure;
        var items = [], issues = [];

        (elements || []).forEach(function (el, idx) {
            if (el.hidden) return;
            var box = resolveBox(el, label);
            var text = ctx.resolve ? ctx.resolve(el) : (el.text || '');

            if (el.type === 'text') {
                if (String(text).trim() === '' && el.hideWhenEmpty !== false) return;
                var style = assign({}, el, { size: mmSize(el, label) });
                var fit = fitText(text, box, style, measure);
                if (!fit.fits) {
                    issues.push({ code: 'text_overflow', element: idx, name: el.name || null, text: String(text) });
                } else if (fit.shrunk && fit.size < LEGIBLE_MM) {
                    // It fits, but only because it was shrunk past the point
                    // where a printed label is comfortably read. Saying so is
                    // the difference between an honest tool and one that hands
                    // over 65 stickers nobody can read.
                    issues.push({
                        code: 'text_tiny', element: idx, name: el.name || null,
                        text: String(text), size: Math.round(fit.size * 10) / 10,
                    });
                }
                items.push({
                    kind: 'text', box: box, lines: fit.lines, size: fit.size,
                    // The baseline offset travels WITH the item. Each backend
                    // asking its own library for an ascent is how the preview
                    // and the PDF ended up 4 mm apart (see render.js/ascentEm).
                    ascentEm: (measure.ascentEm ? measure.ascentEm(style) : 0.8),
                    lineHeight: fit.lineHeight, align: el.align || 'center',
                    valign: el.valign || 'middle', color: el.color || '#000000',
                    font: el.font || 'noto', bold: !!el.bold, italic: !!el.italic,
                    letterSpacing: num(el.letterSpacing, 0), el: el,
                });
            } else if (el.type === 'barcode') {
                var bc = {
                    kind: 'barcode', box: box, symbology: el.symbology || 'qrcode',
                    value: String(text), color: el.color || '#000000',
                    showText: el.showText !== false, el: el,
                };
                /* A code sized in PRINTER DOTS rather than fitted to its box —
                   the GS1 DataMatrix of a marking code (matrix.js explains
                   why). The dpi is the job's (one printer per run), the module
                   may be forced per element; both backends size the symbol
                   from these same fields through LabelMatrix. */
                if (bc.symbology === 'gs1datamatrix' || el.dots != null) {
                    bc.matrix = true;
                    bc.gs1 = bc.symbology === 'gs1datamatrix' || !!el.gs1;
                    bc.dpi = num(ctx.dpi, 203);
                    bc.dots = el.dots != null ? num(el.dots, null) : (ctx.dots != null ? num(ctx.dots, null) : null);
                    bc.quietMm = num(el.quietMm, 2);
                    bc.minModuleMm = el.minModuleMm != null ? num(el.minModuleMm, null) : (ctx.minModuleMm != null ? num(ctx.minModuleMm, null) : null);
                    bc.maxModuleMm = el.maxModuleMm != null ? num(el.maxModuleMm, null) : (ctx.maxModuleMm != null ? num(ctx.maxModuleMm, null) : null);
                    bc.align = el.align || 'center';
                    bc.valign = el.valign || 'middle';
                }
                items.push(bc);
            } else if (el.type === 'image') {
                if (!el.src) return;
                items.push({ kind: 'image', box: box, src: el.src, fit: el.fit || 'contain', el: el });
            } else if (el.type === 'shape') {
                items.push({
                    kind: 'shape', box: box, shape: el.shape || 'rect',
                    fill: el.fill || null, stroke: el.stroke || null,
                    strokeWidth: num(el.strokeWidth, 0.3), radius: num(el.radius, 0), el: el,
                });
            }
        });

        // Nothing may sit outside the label at all — that is ink on the backing
        // paper or on the neighbouring sticker.
        items.forEach(function (it, i) {
            if (it.box.x < -0.01 || it.box.y < -0.01 ||
                it.box.x + it.box.w > label.w + 0.01 ||
                it.box.y + it.box.h > label.h + 0.01) {
                issues.push({ code: 'outside_label', element: i });
            }
        });

        return { items: items, issues: issues };
    }

    /* Font size may be given absolutely (mm) or relative to the label height,
     * which is what lets one preset work on a 21 mm and a 74 mm label. */
    function mmSize(el, label) {
        if (el.sizeRel != null) return Math.max(MIN_FONT_MM, num(el.sizeRel, 0.2) * label.h);
        return Math.max(MIN_FONT_MM, num(el.size, 3));
    }

    /* ── the pre-flight ──────────────────────────────────────────────────────
     * Run every record through the layout and report which ones do not fit.
     * This is the check that turns "the sheet came out wrong" into "record 17
     * is too long", BEFORE the paper is spent.
     */
    function auditRun(label, elements, records, ctx) {
        ctx = ctx || {};
        var bad = [], tiny = [], shrunk = 0, checked = 0;
        for (var i = 0; i < records.length; i++) {
            var rec = records[i];
            if (rec == null) continue;                    // skipped cell
            checked++;
            var res = layoutLabel(label, elements, assign({}, ctx, {
                resolve: function (el) { return ctx.resolve ? ctx.resolve(el, rec, i) : (el.text || ''); },
            }));
            var over = res.issues.filter(function (x) { return x.code === 'text_overflow'; });
            if (over.length) {
                bad.push({ index: i, record: rec, fields: over.map(function (o) { return o.name || o.element; }) });
            }
            var small = res.issues.filter(function (x) { return x.code === 'text_tiny'; });
            if (small.length) tiny.push({ index: i, size: small[0].size });
            res.items.forEach(function (it) {
                if (it.kind === 'text' && it.el && it.size < mmSize(it.el, label) - 1e-9) shrunk++;
            });
            if (bad.length >= 50) break;                  // enough to act on
        }
        return {
            checked: checked, overflow: bad, tiny: tiny,
            shrunkCount: shrunk, ok: bad.length === 0 && tiny.length === 0,
        };
    }

    /* A crude metric used only by tests and as a last-resort fallback: mean
     * Latin glyph advance is about 0.5 em. Never good enough for paper, which
     * is why the real measurer is injected. */
    function defaultMeasure(text, style) {
        return String(text).length * num(style && style.size, 3) * 0.5;
    }

    function num(v, d) {
        v = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : v;
        return (typeof v === 'number' && isFinite(v)) ? v : d;
    }
    function assign(t) {
        for (var i = 1; i < arguments.length; i++) {
            var s = arguments[i];
            if (!s) continue;
            for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k];
        }
        return t;
    }

    return {
        SAFE_MM: SAFE_MM,
        MIN_FONT_MM: MIN_FONT_MM,
        LEGIBLE_MM: LEGIBLE_MM,
        wrapText: wrapText,
        fitText: fitText,
        resolveBox: resolveBox,
        layoutLabel: layoutLabel,
        auditRun: auditRun,
        defaultMeasure: defaultMeasure,
    };
});
